import { useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuthContext } from "@/components/AuthProvider";
import { KEYCLOAK_LOGIN_URL } from "@/config/auth";
import { usePublicFAQ } from "@/hooks/usePublicFAQ";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Share2, LogIn, HelpCircle, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PublicFAQ = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuthContext();
  const navigate = useNavigate();
  const { data: faq, isLoading: isFaqLoading, error } = usePublicFAQ(slug);
  const redirectedRef = useRef(false);

  // Redirect logged-in users to the protected route
  useEffect(() => {
    // Apenas redireciona se: não está carregando auth, está autenticado, tem slug, e ainda não redirecionou
    if (!isAuthLoading && isAuthenticated && slug && !redirectedRef.current) {
      redirectedRef.current = true;
      // Usar timeout para evitar race condition com renderização
      setTimeout(() => {
        navigate(`/perguntasfrequentes/${slug}`, { replace: true });
      }, 0);
    }
  }, [isAuthenticated, isAuthLoading, slug, navigate]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copiado para a área de transferência!");
    } catch {
      toast.error("Não foi possível copiar o link.");
    }
  };

  // Se usuário logado, mostrar loading de redirecionamento
  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Redirecionando...</p>
        </div>
      </div>
    );
  }

  // Loading state - só mostra se REALMENTE está carregando
  if (isFaqLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Carregando FAQ...</p>
        </div>
      </div>
    );
  }

  // Error or not found state
  if (error || !faq) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-6 w-6 text-primary" />
              <span className="font-semibold text-lg">Perguntas Frequentes</span>
            </div>
          </div>
        </header>
        
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto text-center space-y-4">
            <h1 className="text-2xl font-bold text-foreground">FAQ não encontrada</h1>
            <p className="text-muted-foreground">
              A pergunta frequente que você está procurando não existe ou foi removida.
            </p>
            <Button asChild>
              <a href={KEYCLOAK_LOGIN_URL}>
                <LogIn className="h-4 w-4 mr-2" />
                Fazer login para ver mais
              </a>
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'Coletas': 'Coletas',
      'Dados': 'Dados',
      'Documentação': 'Documentação',
      'Geral': 'Geral',
      'Usuários e permissões': 'Usuários e permissões'
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-6 w-6 text-primary" />
              <span className="font-semibold text-lg">Perguntas Frequentes</span>
            </div>
            <Button variant="outline" size="sm" asChild>
              <a href={KEYCLOAK_LOGIN_URL}>
                <LogIn className="h-4 w-4 mr-2" />
                Entrar
              </a>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link 
            to="/perguntasfrequentes" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Ver todas as FAQs
          </Link>

          {/* FAQ Card */}
          <article className="bg-card border rounded-lg p-6 md:p-8 space-y-6">
            {/* Header */}
            <div className="space-y-3">
              <Badge variant="secondary" className="text-xs">
                {getTypeLabel(faq.type)}
              </Badge>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                {faq.title}
              </h1>
            </div>

            {/* Content */}
            <div 
              className="faq-content"
              dangerouslySetInnerHTML={{ __html: faq.content.html }}
            />

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3 pt-4 border-t">
              <Button variant="outline" size="sm" onClick={handleCopyLink}>
                <Share2 className="h-4 w-4 mr-2" />
                Compartilhar
              </Button>
              <Button variant="default" size="sm" asChild>
                <a href={KEYCLOAK_LOGIN_URL}>
                  <LogIn className="h-4 w-4 mr-2" />
                  Fazer login para ver mais
                </a>
              </Button>
            </div>
          </article>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-card mt-auto">
        <div className="container mx-auto px-4 py-6">
          <p className="text-center text-sm text-muted-foreground">
            Precisa de ajuda?{" "}
            <button 
              type="button"
              onClick={() => window.open('https://api.whatsapp.com/send/?phone=5511959590149&text&type=phone_number&app_absent=0', '_blank')}
              className="text-primary hover:underline font-medium"
            >
              Entre em contato com nosso suporte
            </button>
            .
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicFAQ;
